import { Get, Post } from '@/apis/index'
import { EModeType } from '@/config/models'

export interface UserInfo {
  id: string
  name: string
  nickName: string
  xaGuid: string
  phone: string
  xaToken: string
  avatar: string
  realName: string
  orgs: { orgId: string; orgName: string }[]
  orgNames: string
  dataScope: string
  dataPerms: { scope: string | number }[]
  key: string
}
export interface ISimpleUser {
  id: string
  name: string
  phone: string
  nickName: string
}
export interface IRoleUser {
  id: number
  uuid: string
  userId: string
  userName: string
  dataType: number
  dataUuid: string
  readWriteType: EModeType
  operatorId: string
  operatorName: string
  createTime: string
  updateTime: string
  delete: boolean
}

export interface PermissionItem {
  keyPattern: string
  name: string
  hasPerm: boolean
  onlyQuery: number
  childList: PermissionItem[]
}

export interface AccountSecurityPolicy {
  captcha: number
  mfa_sms: number
  mfa_email: number
  // 以下字段由账号中心安全策略下发，登录页按需使用
  pw_length?: number
  pw_complexity?: number
  login_attempts?: number
  lock_time?: number
  mfa_session_expire?: number
}

export interface AccountPublicKey {
  pemId: string
  pemType: string
  pemBits: number
  pemPublic: string
}

export interface AccountCaptcha {
  captcha_id: string
  captcha_image: string
}

/** 图形验证码实时校验结果，账号中心可能返回布尔值，也可能返回对象包装 */
export type CaptchaVerifyResult = boolean | Record<string, unknown> | null

/** MFA 验证通道 */
export type MfaChannel = 'sms' | 'email'

export interface AccountLoginParams {
  login_type: 'mobile' | 'email'
  pem_id: string
  encrypt_data: string
  captcha_id?: string
  captcha?: string
}

export interface SendMfaSmsCodeParams {
  /**
   * MFA 临时令牌。登录挑战中返回的 mobile 是脱敏值(138****1234)，
   * 一并带上 mfa_token 让账号中心可以定位真实手机号
   */
  mfa_token?: string
  icc?: string
  phone?: string
}

export interface SendMfaEmailCodeParams {
  /** MFA 挑战令牌及原始邮箱，页面展示脱敏不改变此值 */
  mfa_token?: string
  email?: string
  captcha_id?: string
  captcha?: string
}

export interface VerifyAccountCaptchaParams {
  captcha_id: string
  captcha: string
}

export interface VerifyAccountMfaParams {
  mfa_token: string
  sms_code?: string
  email_code?: string
  captcha_id?: string
  captcha?: string
}

export interface MfaSubmitPayload {
  sms_code?: string
  email_code?: string
  captcha?: string
}

export interface MfaChallenge {
  mfa_token: string
  mfa_sms: number
  mfa_email: number
  icc?: string
  mobile?: string
  email?: string
}

export type AccountLoginResult = UserInfo | MfaChallenge

export const Login = Post<UserInfo>(`/device-devops-platform/auth/login`, { noHeader: true })

const AccountMfaBaseUrl = `/device-devops-platform/auth/v2`

export const GetAccountSecurityPolicy = Get<AccountSecurityPolicy>(`${AccountMfaBaseUrl}/security/policy`, { noHeader: true })

export const GetAccountPublicKey = Get<AccountPublicKey>(`${AccountMfaBaseUrl}/security/xag/public/key`, { noHeader: true })

export const GetAccountCaptcha = Post<AccountCaptcha>(`${AccountMfaBaseUrl}/security/captcha/image`, { noHeader: true })

export const VerifyAccountCaptcha = (params: VerifyAccountCaptchaParams, headers: Record<string, string> = {}) => Post<CaptchaVerifyResult>(`${AccountMfaBaseUrl}/security/captcha/image/verify`, { noHeader: true })(params, headers)

export const AccountEncryptLogin = (params: AccountLoginParams, headers: Record<string, string> = {}) => Post<AccountLoginResult>(`${AccountMfaBaseUrl}/security/encrypt/login`, { noHeader: true })(params, headers)

export const SendMfaSmsCode = (params: SendMfaSmsCodeParams, headers: Record<string, string> = {}) => Post(`${AccountMfaBaseUrl}/user/sms/code`, { noHeader: true })(params, headers)

export const SendMfaEmailCode = (params: SendMfaEmailCodeParams, headers: Record<string, string> = {}) => Post(`${AccountMfaBaseUrl}/user/email/code`, { noHeader: true })(params, headers)

export const VerifyAccountMfa = (params: VerifyAccountMfaParams, headers: Record<string, string> = {}) => Post<UserInfo>(`${AccountMfaBaseUrl}/security/mfa/verify`, { noHeader: true })(params, headers)

export const Logout = Get(`/device-devops-platform/auth/logout`)

export const GetUserAppActions = Get<PermissionItem[]>(`/device-devops-platform/auth/getMenus`)

export const GetUsers = Get<ISimpleUser[]>(`/device-devops-platform/auth/getUserList`)
